import {
  ARCHETYPE_COUNT,
  CHUNK_SIZE_UNITS,
  ITEMS_PER_CHUNK,
  MAX_WORLD_INSTANCES,
  STREAM_RADIUS_CHUNKS,
  WORLD_SEED,
  getTier,
} from "./config";
import { SeededRandom, hashNumbers } from "./random";
import { terrainHeightMeters } from "./TokyoTerrain";

export interface WorldItem {
  id: number;
  chunkX: number;
  chunkZ: number;
  ordinal: number;
  xMeters: number;
  zMeters: number;
  surfaceYMeters: number;
  radiusMeters: number;
  heightScale: number;
  archetype: number;
  colorIndex: number;
  rotation: number;
  name: string;
  rare: boolean;
  rareId: number | null;
  active: boolean;
  renderSlot: number;
}

export interface GeneratedWorld {
  readonly centerChunkX: number;
  readonly centerChunkZ: number;
  readonly tierIndex: number;
  readonly chunkSizeMeters: number;
  readonly items: WorldItem[];
}

const COLOR_VARIANTS = 7;
const RARE_NAMES = [
  "Golden Maneki-neko",
  "Tiny Hachiko",
  "Rainbow Daruma",
  "Retro Game Cart",
  "Sushi Trophy",
  "Mini Lantern",
  "Crystal Gacha",
  "Tokyo Train Pin",
  "Lucky Taiyaki",
  "Kabuki Mask",
  "Stack-chan",
  "Sky Blue Camera",
  "Secret Sol Star",
] as const;

export function chunkSizeMetersForTier(tierIndex: number): number {
  return getTier(tierIndex).metersPerUnit * CHUNK_SIZE_UNITS;
}

export function chunkCoordinate(positionMeters: number, tierIndex: number): number {
  return Math.floor(positionMeters / chunkSizeMetersForTier(tierIndex));
}

export function generateChunk(
  tierIndex: number,
  chunkX: number,
  chunkZ: number,
  worldSeed = WORLD_SEED,
): WorldItem[] {
  const items: WorldItem[] = [];
  fillChunk(items, 0, tierIndex, chunkX, chunkZ, ITEMS_PER_CHUNK, worldSeed);
  return items;
}

function fillChunk(
  target: WorldItem[],
  startIndex: number,
  tierIndex: number,
  chunkX: number,
  chunkZ: number,
  itemCount: number,
  worldSeed: number,
): number {
  const tier = getTier(tierIndex);
  const chunkSizeMeters = chunkSizeMetersForTier(tierIndex);
  const random = new SeededRandom(hashNumbers(worldSeed, tierIndex, chunkX, chunkZ));
  const archetypeOffset = random.integer(0, ARCHETYPE_COUNT);

  for (let ordinal = 0; ordinal < itemCount; ordinal += 1) {
    const sizeCurve = random.next() ** 1.42;
    const radiusUnits = 0.1 + sizeCurve * 1.8;
    const archetype = (ordinal + archetypeOffset) % ARCHETYPE_COUNT;
    const targetIndex = startIndex + ordinal;
    const id = hashNumbers(worldSeed, tierIndex, chunkX, chunkZ, ordinal);
    const xMeters = (chunkX + 0.06 + random.next() * 0.88) * chunkSizeMeters;
    const zMeters = (chunkZ + 0.06 + random.next() * 0.88) * chunkSizeMeters;
    const radiusMeters = radiusUnits * tier.metersPerUnit;
    const heightScale = 0.72 + random.next() * 1.15;
    const colorIndex = random.integer(0, COLOR_VARIANTS);
    const rotation = random.next() * Math.PI * 2;
    const rareRoll = hashNumbers(id, 0x52_41_52_45);
    const rare = rareRoll % 89 === 0;
    const rareId = rare ? rareRoll % RARE_NAMES.length : null;
    const name = rare && rareId !== null
      ? RARE_NAMES[rareId] ?? "Tokyo Treasure"
      : tier.itemNames[archetype] ?? "Scale Fragment";
    const surfaceYMeters = terrainHeightMeters(xMeters, zMeters, tierIndex);
    const existing = target[targetIndex];
    if (existing) {
      existing.id = id;
      existing.chunkX = chunkX;
      existing.chunkZ = chunkZ;
      existing.ordinal = ordinal;
      existing.xMeters = xMeters;
      existing.zMeters = zMeters;
      existing.surfaceYMeters = surfaceYMeters;
      existing.radiusMeters = radiusMeters;
      existing.heightScale = heightScale;
      existing.archetype = archetype;
      existing.colorIndex = colorIndex;
      existing.rotation = rotation;
      existing.name = name;
      existing.rare = rare;
      existing.rareId = rareId;
      existing.active = true;
      existing.renderSlot = -1;
    } else {
      target.push({
        id,
        chunkX,
        chunkZ,
        ordinal,
        xMeters,
        zMeters,
        surfaceYMeters,
        radiusMeters,
        heightScale,
        archetype,
        colorIndex,
        rotation,
        name,
        rare,
        rareId,
        active: true,
        renderSlot: -1,
      });
    }
  }
  return startIndex + itemCount;
}

export function generateWorldAround(
  tierIndex: number,
  playerXMeters: number,
  playerZMeters: number,
  density = 1,
  worldSeed = WORLD_SEED,
): GeneratedWorld {
  return fillWorldAround([], tierIndex, playerXMeters, playerZMeters, density, worldSeed);
}

export function fillWorldAround(
  target: WorldItem[],
  tierIndex: number,
  playerXMeters: number,
  playerZMeters: number,
  density = 1,
  worldSeed = WORLD_SEED,
): GeneratedWorld {
  const centerChunkX = chunkCoordinate(playerXMeters, tierIndex);
  const centerChunkZ = chunkCoordinate(playerZMeters, tierIndex);
  const chunkSizeMeters = chunkSizeMetersForTier(tierIndex);
  const keepPerChunk = Math.max(8, Math.min(ITEMS_PER_CHUNK, Math.round(ITEMS_PER_CHUNK * density)));
  let cursor = 0;

  for (let offsetZ = -STREAM_RADIUS_CHUNKS; offsetZ <= STREAM_RADIUS_CHUNKS; offsetZ += 1) {
    for (let offsetX = -STREAM_RADIUS_CHUNKS; offsetX <= STREAM_RADIUS_CHUNKS; offsetX += 1) {
      cursor = fillChunk(
        target,
        cursor,
        tierIndex,
        centerChunkX + offsetX,
        centerChunkZ + offsetZ,
        keepPerChunk,
        worldSeed,
      );
    }
  }
  target.length = cursor;

  if (target.length > MAX_WORLD_INSTANCES) {
    throw new Error(`Generated ${target.length} items over pool cap ${MAX_WORLD_INSTANCES}`);
  }

  return { centerChunkX, centerChunkZ, tierIndex, chunkSizeMeters, items: target };
}
